<template>
  <div class="container">
    <div class="overview__date" ref="overview__date">
      <div class="overview__item">
        <div class="overview__date-group">
          <icon-base
            class="input-group-addon"
            view-box="0 0 32 32"
            icon-name="calendar"
          >
            <IconCalendar />
          </icon-base>
          <FlatPicker
            class="overview__date-trigger"
            :value="dateRange"
            :config="datePickerConfig"
            @on-change="handleDateChange"
          />
        </div>
      </div>
    </div>

    <div class="overview__filter" ref="overview__filter">
      <div
        v-for="(value, key) in status"
        class="overview__item"
        :class="{
          [`btn-overview--${key}`]: true
        }"
        :key="`overview__filter-${key}`"
      >
        <button
          v-if="value.status === 'success'"
          type="button"
          class="btn-overview"
          :class="{
            'btn-overview--active': filter.status === key
          }"
          @click.prevent="filter.status = key"
        >
          <header class="overview__header">
            <div class="overview__title">
              <span class="overview__title-text">{{ key | ucfirst }}</span>
              <span class="overview__badge">{{ value.count }}</span>
            </div>
          </header>

          <div class="overview__body">
            <div class="overview__amount">
              {{ value.totalAmount | currency }}
            </div>
          </div>
        </button>

        <div v-else-if="value.status === 'pending'" class="btn-overview">
          <Spinner :theme="theme.primary" />
        </div>

        <div v-else class="btn-overview">
          Something went wrong.
        </div>
      </div>
    </div>

    <div class="row overview__search" ref="overview__search">
      <div class="col-md-12 col-lg-6">
        <SearchBar
          class="pt-0"
          placeholder="Search Member"
          @search="keyword => (filter.user = keyword)"
        />
      </div>
    </div>

    <div class="overview__list">
      <Spinner v-if="isLoading" :theme="theme.primary" />

      <template v-else>
        <Scrollbar
          class="scrollbar--primary overflow__wrapper"
          ref="scrollview"
        >
          <div class="overflow__header">
            <table class="table">
              <thead>
                <tr>
                  <th style="width: 20%;">Date</th>
                  <th>Member</th>
                  <th style="width: 20%;">Invoice ID</th>
                  <th style="width: 20%;">Amount</th>
                  <th style="width: 15%;">Payment Status</th>
                </tr>
              </thead>

              <transition-group
                v-if="list.status !== 'error'"
                tag="tbody"
                name="list"
              >
                <tr
                  v-for="(item, index) in list.items"
                  :key="`item-${item.id}-${index}`"
                  class="overview__item"
                  :data-index="index"
                >
                  <td class="item__timestamp">{{ item.createdAt | date }}</td>
                  <td class="item__user">{{ item.User | fullName }}</td>
                  <td class="item__invoice-id">
                    <a
                      :style="{ color: theme.primary }"
                      @click="handleViewInvoice(item.userId, item.id)"
                      class="tag-link"
                      style="cursor: pointer"
                    >
                      {{ item.code }}
                    </a>
                  </td>
                  <td class="item__amount">
                    {{ item.totalAmount | currency }}
                  </td>
                  <td class="item__status">
                    <span
                      class="font-w700"
                      :class="{
                        'text-success': getStatus(item) === 'paid',
                        'text-warning': getStatus(item) === 'cancelled',
                        'text-danger': getStatus(item) === 'unpaid'
                      }"
                    >
                      {{ getStatus(item) | ucfirst }}
                    </span>
                  </td>
                </tr>

                <tr
                  v-if="list.status === 'pending'"
                  class="text-center list-fetching"
                  :key="'list-fetching'"
                  :data-index="list.items.length"
                >
                  <td colspan="6">
                    <Spinner :theme="theme.primary" />
                  </td>
                </tr>

                <tr
                  v-if="list.status === 'success' && list.items.length === 0"
                  class="text-center list-end"
                  :key="'list-no-results'"
                  :data-index="list.items.length"
                >
                  <td colspan="6">No results found.</td>
                </tr>
              </transition-group>

              <tbody v-else>
                <tr class="text-center">
                  <td colspan="6">Something went wrong.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Scrollbar>
      </template>
    </div>
  </div>
</template>

<script>
import FlatPicker from "vue-flatpickr-component";
import Scrollbar from "@/components/Scrollbar";
import SearchBar from "@/components/SearchBar";
import { format as dateFormatter, startOfMonth, endOfMonth } from "date-fns";
import { upperFirst } from "lodash";
import qs from "qs";
import { mapActions, mapGetters } from "vuex";

const Spinner = () => import("@/components/Spinner");
const IconCalendar = () => import("@/components/icons/IconCalendarDots");

const END_DAY_IN_SECONDS = 86399;

function fetchListURL(params) {
  let { user, page, ...rest } = params;
  const query = qs.stringify(
    {
      search: { ...rest, member: user },
      page
    },
    { skipNulls: true }
  );

  return `/orders/fetchReportList?${query}`;
}

function fetchAmountURL(params) {
  let { user, ...rest } = params;
  const query = qs.stringify(
    {
      search: { ...rest, member: user }
    },
    { skipNulls: true }
  );
  return `/orders/fetchTotalAmount?${query}`;
}

export default {
  name: "AdminReportsOverview",
  components: {
    SearchBar,
    IconCalendar,
    Spinner,
    Scrollbar,
    FlatPicker
  },
  data() {
    return {
      isLoading: true,
      filter: {
        status: "all",
        startAt: startOfMonth(Date.now()),
        endAt: endOfMonth(Date.now()),
        user: ""
      },
      status: {
        all: { status: "pending", totalAmount: 0, count: 0 },
        paid: { status: "pending", totalAmount: 0, count: 0 },
        unpaid: { status: "pending", totalAmount: 0, count: 0 },
        cancelled: { status: "pending", totalAmount: 0, count: 0 }
      },
      list: {
        status: "pending",
        items: [],
        error: null,
        limit: 10,
        page: {
          total: 1,
          number: 1,
          size: 20
        }
      },
      dateRange: null,
      datePickerConfig: {
        dateFormat: "M j, Y",
        mode: "range",
        monthSelectorType: "static",
        yearSelectorType: "static",
        wrap: true
      },
      error: null
    };
  },
  filters: {
    date(timestamp) {
      return dateFormatter(new Date(timestamp), "MMM D, YYYY");
    },
    ucfirst(string) {
      return upperFirst(string);
    },
    fullName(user) {
      return user.firstName + " " + user.lastName;
    },
    currency(amount) {
      const newFormat = Number(amount.toFixed(2)).toLocaleString("en-US", {
        minimumFractionDigits: 2
      });
      return `PHP ${newFormat}`;
    }
  },
  watch: {
    "filter.status": function() {
      this.resetScroll();
      this.resetList();
      this.initialFetchItem();
    },
    "filter.user": function() {
      this.resetScroll();
      this.resetList();
      Promise.all([
        this.handleFetchFilterData("all"),
        this.handleFetchFilterData("paid"),
        this.handleFetchFilterData("unpaid"),
        this.handleFetchFilterData("cancelled"),
        this.initialFetchItem()
      ]);
    },
    "filter.endAt": function() {
      this.resetScroll();
      this.resetList();
      Promise.all([
        this.handleFetchFilterData("all"),
        this.handleFetchFilterData("paid"),
        this.handleFetchFilterData("unpaid"),
        this.handleFetchFilterData("cancelled"),
        this.initialFetchItem()
      ]);
    }
  },
  computed: {
    theme() {
      return this.$store.state.app.scheme;
    }
  },
  created() {
    if (typeof this.getFilters().status != "undefined") {
      this.filter = this.getFilters();
    }
    this.dateRange = [this.filter.startAt, this.filter.endAt];
  },
  mounted() {
    this.isLoading = false;

    this.resetList();
    Promise.all([
      this.handleFetchFilterData("all"),
      this.handleFetchFilterData("paid"),
      this.handleFetchFilterData("unpaid"),
      this.handleFetchFilterData("cancelled"),
      this.handleFetchList({ size: 40, number: 2 })
    ]);

    this.$nextTick(() => {
      document
        .querySelector(".flatpickr-calendar")
        .classList.add("staticYear", "staticMonth");
      document
        .querySelector(".flatpickr-calendar.staticYear .numInput")
        .setAttribute("readonly", true);

      this.$refs.scrollview.$el.addEventListener(
        "scroll",
        this.handleTableScroll,
        false
      );

      window.addEventListener("resize", this.setListContainerHeight, false);
      window.dispatchEvent(new Event("resize"));

      document.documentElement.style.setProperty(
        "--primary",
        this.theme.primary
      );
    });
  },
  beforeDestroy() {
    this.$refs.scrollview.$el.removeEventListener(
      "scroll",
      this.handleTableScroll,
      false
    );
    window.removeEventListener("resize", this.setListContainerHeight, false);
  },
  methods: {
    ...mapActions({
      SET_REPORT_FILTER: "SET_REPORT_FILTER",
      SET_REPORT_HISTORY: "SET_REPORT_HISTORY"
    }),
    ...mapGetters({
      getFilters: "getFilters",
      getReportHistory: "getReportHistory"
    }),
    resetScroll() {
      if (typeof this.getFilters().status == "undefined") {
        this.$refs.scrollview.$el.scrollTop = 0;
      }
    },
    getStatus(invoice) {
      if (invoice.paidAt) return "paid";
      if (invoice.cancelledAt) return "cancelled";
      if (!invoice.paidAt && !invoice.cancelledAt) return "unpaid";
    },
    resetList() {
      this.list.items = [];
      this.list.page.number = 1;
    },
    async handleFetchFilterData(type) {
      this.status[type].status = "pending";
      try {
        const {
          data: { data }
        } = await this.$api(fetchAmountURL({ ...this.filter, status: type }));
        this.status[type] = data;
        this.status[type].status = "success";
      } catch (error) {
        this.error = error;
        this.status[type].status = "error";
      }

      return this.status[type];
    },
    async handleFetchList(params) {
      this.list.status = "pending";
      let { number, size } = this.list.page;

      if (params && params.size) size = params.size;
      try {
        const {
          data: { data, meta }
        } = await this.$api(
          fetchListURL({ ...this.filter, page: { number, size } })
        );

        this.list.status = "success";
        this.list.page.total = meta.totalPages;

        if (this.list.page.total === number) {
          this.list.status = "maxed";
        }

        this.list.items = this.list.items.concat(...data);
      } catch (error) {
        this.list.status = "error";
      } finally {
        this.setListContainerHeight();
      }
      if (params && params.number) this.list.page.number = params.number;
    },
    shouldAppendItems(scrollHeight, scrollPosition, height) {
      const offset = height * 0.5;
      return (
        scrollHeight - scrollPosition <= height + offset &&
        this.list.items.length
      );
    },
    handleTableScroll({ target }) {
      this.scrollPosition = target.scrollTop;

      if (this.list.page.number >= this.list.page.total) {
        this.list.status = "maxed";
        return;
      }

      if (
        this.shouldAppendItems(
          target.scrollHeight,
          this.scrollPosition,
          target.clientHeight
        ) &&
        this.list.status === "success"
      ) {
        this.list.page.number += 1;
        this.handleFetchList();
      }
    },
    setListContainerHeight() {
      const {
        overview__date: datePickerContainerEl,
        overview__filter: filterContainerEl,
        overview__search: searchContainerEl
      } = this.$refs;
      const headerEl = document.querySelector("nav.header");
      let offsetHeight = searchContainerEl.clientHeight;

      if (this.list.items.length === 0) {
        offsetHeight =
          headerEl.clientHeight +
          datePickerContainerEl.clientHeight +
          filterContainerEl.clientHeight +
          searchContainerEl.clientHeight;
      }

      this.$refs.scrollview.$el.style.maxHeight = `calc(100vh - 1em - ${offsetHeight}px)`;
    },
    handleDateChange(selectedDates) {
      let [startAt, endAt] = selectedDates;

      if (Boolean(endAt) === false) return;

      if (endAt) {
        // Set end date time to 23:59:59;
        endAt.setSeconds(END_DAY_IN_SECONDS);
      }

      if (Boolean(endAt) === false) {
        // Single date is selected, set `endAt` default to startAt + 23:59:59
        endAt = new Date(new Date(startAt).setSeconds(END_DAY_IN_SECONDS));
      }

      this.filter.startAt = startAt;
      this.filter.endAt = new Date(endAt);
    },
    handleViewInvoice(userId, orderId) {
      this.SET_REPORT_FILTER(this.filter);
      this.SET_REPORT_HISTORY(1);
      this.$router.push({
        path: `/admin/booking/${userId}/invoice/${orderId}`
      });
    },
    initialFetchItem() {
      if (this.getReportHistory() === 0) {
        this.handleFetchList({ size: 40, number: 2 });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
thead {
  color: $gray-900;
  font-family: $headings-font-family;
  font-weight: 400;
}

.table {
  font-family: "Gotham Book", sans-serif;
  font-size: $font-size-sm;
  font-weight: 400;
}

.table thead th {
  text-transform: inherit;
}

.table th,
.table td {
  border-top: 0;
}

.table th {
  background-color: $gray-50;
  position: sticky;
  top: 0;

  &:first-child {
    padding-top: 1.625em;
    padding-left: 1.625em;
  }

  &:last-child {
    padding-top: 1.625em;
    padding-right: 1.625em;
  }
}

.table td {
  &:first-child {
    padding-left: 1.625em;
  }

  &:last-child {
    padding-right: 1.625em;
  }
}

.overview {
  &__filter {
    display: flex;
    flex-flow: row wrap;
    margin: 0 -0.46875rem;
  }

  &__item {
    flex: 100%;
    padding: 0 0.46875rem;

    @include media-breakpoint-up(md) {
      flex: 50%;
    }

    @include media-breakpoint-up(lg) {
      flex: 25%;
    }
  }

  &__header {
    height: auto;
  }

  &__title {
    &-text {
      display: inline-block;
      vertical-align: middle;
    }
  }

  &__body {
    margin-top: 0.75em;
  }

  &__amount {
    font-size: $font-size-md;
  }

  &__badge {
    background-color: $brand-primary;
    background-color: var(--primary, $brand-primary);
    border-radius: 1.625rem;
    color: $white;
    display: inline-block;
    font-size: 0.875rem;
    font-weight: 400;
    height: 1.625rem;
    line-height: 1.625rem;
    margin-left: 0.75rem;
    min-width: 1.625rem;
    padding: 0 0.625em;
    text-align: center;
    transition: background 200ms ease, color 200ms ease;
    vertical-align: middle;
    white-space: nowrap;
  }

  &__list {
    background-color: $gray-50;
  }

  &__date {
    display: flex;
    flex-flow: row wrap;
    margin: 0 -0.46875rem;

    .overview__item {
      flex: 100%;

      @include media-breakpoint-up(md) {
        flex: 0 0 50%;
      }

      @include media-breakpoint-up(lg) {
        flex: 0 0 calc(25% + 2ch);
      }
    }
  }

  &__date {
    &-trigger {
      background-color: $brand-secondary;
      background-color: var(--secondary, $brand-secondary);
      background-repeat: no-repeat;
      border-color: transparent;
      border-radius: 0.3125em;
      color: $brand-primary;
      color: var(--primary, $brand-primary);
      flex: 100%;
      font-weight: 400;
      line-height: 1;
      padding: 0.8125em 1.5em 0.8125em 3.5em;
      text-align: left;
      transition: background 200ms ease, color 200ms ease;
      width: 100%;

      &:hover {
        background-color: $brand-primary;
        background-color: var(--primary, $brand-primary);
        color: $white;
        cursor: pointer;
      }
    }

    &-text {
      display: inline-block;
      vertical-align: middle;
    }

    &-group {
      color: $brand-primary;
      color: var(--primary, $brand-primary);
      margin-bottom: 1em;
      position: relative;
      transition: background 200ms ease, color 200ms ease;

      &:hover {
        color: $white;
      }

      .input-group-addon {
        font-size: 12px;
        left: 2em;
        pointer-events: none;
        position: absolute;
        top: 1em;
      }
    }
  }
}

.btn {
  &-overview {
    align-items: center;
    background-color: transparent;
    border: 1px solid $brand-primary;
    border: 1px solid var(--primary, $brand-primary);
    border-radius: 0.375rem;
    display: flex;
    flex-flow: column wrap;
    justify-content: center;
    line-height: 1em;
    margin-bottom: 1rem;
    min-height: 5.375rem;
    padding: 0.9375em;
    transition: background 200ms ease, color 200ms ease;
    width: 100%;

    &:hover {
      background-color: $brand-secondary;
      background-color: var(--secondary, $brand-secondary);
      color: $gray-700;
      cursor: pointer;
    }

    &--active {
      background-color: $brand-primary;
      background-color: var(--primary, $brand-primary);
      color: $white;

      &:hover {
        .overview__badge {
          background-color: $brand-primary;
          background-color: var(--primary, $brand-primary);
          color: $white;
        }
      }

      .overview__badge {
        background-color: $white;
        color: $brand-primary;
        color: var(--primary, $brand-primary);
      }
    }
  }
}

.overflow {
  &__wrapper {
    overflow: auto;
  }
}

.list-enter-active,
.list-leave-active {
  opacity: 1;
  transition: opacity 200ms;
}

.list-enter,
.list-leave-to {
  opacity: 0;
}

.tag-link {
  cursor: pointer;
  &:hover {
    text-decoration: underline !important;
  }
}
</style>
